import {
  IDashboardRowFieldsFragment,
  IScheduleFieldsFragment,
  IScheduleHistoryFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import useDashboardSelector from '../../../redux/selectorHooks/useDashboardSelector';
import { useCallback, useMemo } from 'react';
import { byTypeName, GraphQLUnionMember } from '../../../utils/types';
import { queryTypeConfigLookup } from '../../../utils/queryTypeLookup';
import { globalFormat } from '../../../utils/formatting';
import { MaybeNil } from '../../../utils/interfaces';
import { IReportItem } from './ReportAlertSelectMenu';

type IScheduleDashboardAlertAnalysis = GraphQLUnionMember<
  IScheduleFieldsFragment['entity'],
  'DashboardScheduleEntity'
>['alertAnalyses'][number];

const mapReportToReportItem = (
  analysis: IScheduleDashboardAlertAnalysis,
  dateFormatter: (date: string) => string,
): IReportItem => ({
  value: analysis.id,
  label: analysis.display,
  icon: queryTypeConfigLookup[analysis.queryType].icon,
  createdAt: dateFormatter(analysis.createdAt),
});

const useScheduleReportForSelection = (
  currentSchedule: MaybeNil<
    IScheduleFieldsFragment & IScheduleHistoryFieldsFragment
  >,
) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();

  const dateFormatter = useCallback(
    (apiDate: string) => {
      return globalFormat.formatDate(apiDate, { dateFormat, timeFormat });
    },
    [dateFormat, timeFormat],
  );

  const dashboard = useDashboardSelector('dashboard');
  const generateReportItemsFromDashboardRow = useCallback(
    (row: IDashboardRowFieldsFragment) => {
      return row.columns
        ?.filter(byTypeName('DashboardAnalysisColumn'))
        .map(({ analysis }) => mapReportToReportItem(analysis, dateFormatter));
    },
    [dateFormatter],
  );

  const allReports = useMemo(() => {
    let rows: MaybeNil<IDashboardRowFieldsFragment[]> = null;
    if (currentSchedule?.entity?.__typename === 'DashboardScheduleEntity') {
      rows = currentSchedule?.entity?.item?.rows;
    } else if (dashboard?.__typename === 'Dashboard') {
      rows = dashboard.rows;
    }

    return (
      rows
        ?.map((row) => {
          return generateReportItemsFromDashboardRow(row);
        })
        .flat() ?? []
    );
  }, [currentSchedule, dashboard, generateReportItemsFromDashboardRow]);

  const currentReportSelection = useMemo(() => {
    let currentSelection: IReportItem[] = [];
    if (currentSchedule?.entity?.__typename === 'DashboardScheduleEntity') {
      currentSelection = currentSchedule.entity.alertAnalyses.map((analysis) =>
        mapReportToReportItem(analysis, dateFormatter),
      );
    }
    if (
      currentSchedule &&
      currentSchedule.alertOnly &&
      currentSelection.length === 0
    ) {
      return allReports;
    } else {
      return currentSelection;
    }
  }, [allReports, currentSchedule, dateFormatter]);

  return { allReports, currentReportSelection };
};

export default useScheduleReportForSelection;
